/**
 * 매장 목록 — 마스터-디테일
 *
 *  /credit/companies 와 동일한 패턴.
 *  좌측: 전체/활성/비활성 탭 + 검색 + 매장 리스트 (무한 스크롤)
 *  우측: 선택된 매장 상세 (탭 구성)
 *
 *  정렬은 서버(dtRegist desc) 순서를 그대로 사용 — 최근 등록된 매장이 위로 온다.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Search, Store, Coins } from 'lucide-react';
import {
  companyApi,
  type CompanyListResponseDto,
  type GetCompaniesResponse,
} from '@/services/api';
import { creditApi } from '@/services/api/credit';
import { StatusBadge } from '@/components/common';
import { BusinessDetailPanel, shouldShowActivationBadge } from './BusinessDetailPanel';

type StatusTab = 'ALL' | 'Y' | 'N' | 'TEST';

const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'Y', label: '활성' },
  { key: 'N', label: '비활성' },
  { key: 'TEST', label: '테스트' },
];

const ACTIVATION_LABEL: Record<string, string> = {
  NOT_REQUESTED: '미요청',
  REQUESTED: '요청',
  AGREED: '승인',
  DENIED: '반려',
};

const ACTIVATION_TONE: Record<string, 'neutral' | 'warning' | 'primary' | 'danger'> = {
  NOT_REQUESTED: 'neutral',
  REQUESTED: 'warning',
  AGREED: 'primary',
  DENIED: 'danger',
};

/** 무한 스크롤 한 번에 불러올 건수 */
const FETCH_SIZE = 30;

/** 바닥에서 이 거리 안으로 들어오면 다음 페이지를 당겨온다 */
const SCROLL_THRESHOLD_PX = 200;

const formatDate = (s?: string) => (s ? new Date(s).toLocaleDateString('ko-KR') : '-');

export function BusinessListPage() {
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState<string | undefined>();
  const [tab, setTab] = useState<StatusTab>('ALL');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);

  const listRef = useRef<HTMLDivElement>(null);

  // 검색어 디바운스 (300ms)
  useEffect(() => {
    const t = setTimeout(() => setAppliedKeyword(keyword.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  const statusParam = tab === 'ALL' ? undefined : tab;

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['companies', 'infinite', { search: appliedKeyword, status: statusParam }],
    queryFn: ({ pageParam }) =>
      companyApi.getCompanies({
        page: pageParam,
        size: FETCH_SIZE,
        search: appliedKeyword,
        status: statusParam,
      }),
    initialPageParam: 0,
    // last/number 값에 의존하지 않고 누적 건수로 판단
    getNextPageParam: (lastPage: GetCompaniesResponse, allPages: GetCompaniesResponse[]) => {
      const loaded = allPages.reduce((n, p) => n + (p.content?.length ?? 0), 0);
      const total = lastPage.totalElements ?? 0;
      if (loaded === 0 || loaded >= total) return undefined;
      return allPages.length;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const companies = useMemo<CompanyListResponseDto[]>(
    () => (data?.pages ?? []).flatMap(p => (p.content ?? []) as CompanyListResponseDto[]),
    [data],
  );
  const totalElements = data?.pages?.[0]?.totalElements ?? 0;

  // 탭 배지용 전체 건수 — 현재 검색어 기준 (size 1 로 count 만 사용)
  const countAll = useTabCount(appliedKeyword, undefined);
  const countActive = useTabCount(appliedKeyword, 'Y');
  const countInactive = useTabCount(appliedKeyword, 'N');
  const countTest = useTabCount(appliedKeyword, 'TEST');
  const tabCounts: Record<StatusTab, number | undefined> = {
    ALL: countAll,
    Y: countActive,
    N: countInactive,
    TEST: countTest,
  };

  // 매장별 총 크레딧 잔액 — 목록 배지용 (한 번 조회 후 companySeq→balance 맵)
  const { data: wallets } = useQuery({
    queryKey: ['companyWallets', 'listBadge'],
    queryFn: () => creditApi.listCompanyWallets(),
    staleTime: 60 * 1000,
  });
  const balanceMap = useMemo(() => {
    const m = new Map<number, number>();
    (wallets ?? []).forEach(w => m.set(w.companySeq, w.balance ?? 0));
    return m;
  }, [wallets]);
  // 면제 종료일 맵 — 잔액과 같은 지갑 응답에서 뽑는다(추가 조회 없음)
  const exemptUntilMap = useMemo(() => {
    const m = new Map<number, string>();
    (wallets ?? []).forEach(w => {
      if (w.exemptUntil) m.set(w.companySeq, w.exemptUntil);
    });
    return m;
  }, [wallets]);

  // 탭/검색이 바뀌면 목록 스크롤을 맨 위로
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [appliedKeyword, tab]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD_PX) {
      fetchNextPage();
    }
  };

  return (
    // 페이지 전체를 한 화면에 — 헤더(64) + main 패딩 고려해 calc
    <div className="flex h-[calc(100vh-120px)] flex-col">
      <header className="mb-3 shrink-0">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
          <Store className="text-gray-500" size={22} /> 매장 관리
        </h1>
        <p className="mt-0.5 text-xs text-gray-500">
          최근 등록된 매장 순으로 표시됩니다. 좌측에서 매장을 선택하면 우측에 상세 정보가 탭으로
          표시됩니다.
        </p>
      </header>

      {/* 좌측 리스트 + 우측 상세 — Master-Detail (flex-1, 내부 스크롤) */}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        {/* ── 좌측: 매장 리스트 (5/12) ── */}
        <div className="col-span-12 flex min-h-0 flex-col lg:col-span-5">
          {/* 검색 */}
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="매장명 / 사업자등록번호 / 지역"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* 상태 탭 */}
          <div className="mb-2 flex shrink-0 gap-1 border-b border-gray-200">
            {STATUS_TABS.map(t => {
              const active = tab === t.key;
              const count = tabCounts[t.key];
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {t.label}
                  {count != null && (
                    <span
                      className={`ml-1 text-xs tabular-nums ${
                        active ? 'text-primary-500' : 'text-gray-400'
                      }`}
                    >
                      {count.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              {companies.length.toLocaleString()} / {totalElements.toLocaleString()}개 표시
            </span>
            {isFetching && !isFetchingNextPage && !isLoading && (
              <span className="text-gray-400">불러오는 중…</span>
            )}
          </div>

          {/* 리스트 — flex-1 로 남은 공간 차지, 내부 스크롤 + 무한 로딩 */}
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="h-full divide-y divide-gray-100 overflow-y-auto"
            >
              {isLoading && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">로딩 중…</div>
              )}
              {!isLoading && error && (
                <div className="px-4 py-8 text-center text-sm text-red-500">
                  매장 목록을 불러오지 못했습니다.
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="ml-2 underline hover:text-red-700"
                  >
                    다시 시도
                  </button>
                </div>
              )}
              {!isLoading && !error && companies.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  {appliedKeyword ? '검색 결과가 없습니다.' : '등록된 매장이 없습니다.'}
                </div>
              )}
              {!isLoading &&
                !error &&
                companies.map(c => {
                  const selected = selectedSeq === c.companySeq;
                  const inactive = c.activeYn !== 'Y';
                  return (
                    <div
                      key={c.companySeq}
                      onClick={() => setSelectedSeq(c.companySeq)}
                      // 컨테이너의 divide-gray-100 규칙(명시도 0,3,0)이 border-color 단축 속성으로
                      // 좌측 테두리 색까지 덮어써서, ! 프리픽스로 선택 표시를 강제한다.
                      className={`cursor-pointer px-3 py-2.5 transition-colors ${
                        selected
                          ? 'border-l-4 !border-l-primary-500 bg-primary-50 pl-2'
                          : 'border-l-4 !border-l-transparent hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={`truncate text-sm font-medium ${
                                inactive ? 'text-gray-500' : 'text-gray-900'
                              }`}
                            >
                              {c.companyName ?? `매장 #${c.companySeq}`}
                            </span>
                            <StatusBadge tone={inactive ? 'neutral' : 'success'}>
                              {inactive ? '비활성' : '활성'}
                            </StatusBadge>
                            {c.testYn === 'Y' && (
                              <StatusBadge tone="danger">TEST</StatusBadge>
                            )}
                            {c.activationRequestStatus &&
                              shouldShowActivationBadge(
                                c.activeYn,
                                c.activationRequestStatus,
                              ) && (
                                <StatusBadge
                                  tone={ACTIVATION_TONE[c.activationRequestStatus] ?? 'neutral'}
                                >
                                  {ACTIVATION_LABEL[c.activationRequestStatus] ?? '-'}
                                </StatusBadge>
                              )}
                            {(() => {
                              const until = exemptUntilMap.get(c.companySeq);
                              if (!until || new Date(until) <= new Date()) return null;
                              const daysLeft = Math.max(
                                1,
                                Math.ceil((new Date(until).getTime() - Date.now()) / 86400000),
                              );
                              return (
                                <span title={`크레딧 차감 면제 · ${formatDate(until)}까지`}>
                                  <StatusBadge tone="info">🛡 면제 D-{daysLeft}</StatusBadge>
                                </span>
                              );
                            })()}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-gray-500">
                            {c.region ? `${c.region} · ` : ''}
                            {c.businessNumber || '사업자번호 없음'}
                            {c.mainTelNumber ? ` · ${c.mainTelNumber}` : ''}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-gray-400">
                            {`${c.companyAddr ?? ''} ${c.companyAddrDetail ?? ''}`.trim() ||
                              '주소 없음'}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-[11px] text-gray-600">
                            {c.ceoName || '대표자 미등록'}
                          </div>
                          <div className="mt-0.5 text-[10px] text-gray-500">
                            사업자 {c.ownerCount ?? 0} · 선생님 {c.teacherCount ?? 0}
                          </div>
                          {balanceMap.has(c.companySeq) &&
                            (() => {
                              const bal = balanceMap.get(c.companySeq) ?? 0;
                              const tone =
                                bal < 10
                                  ? 'bg-red-50 text-red-700 border-red-200'
                                  : bal < 100
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-blue-50 text-blue-700 border-blue-200';
                              return (
                                <div
                                  className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-bold tabular-nums ${tone}`}
                                  title="총 크레딧 잔액"
                                >
                                  <Coins size={12} className="shrink-0" />
                                  {bal.toLocaleString()}
                                  <span className="font-medium opacity-70">크레딧</span>
                                </div>
                              );
                            })()}
                          <div className="mt-0.5 text-[10px] text-gray-400">
                            {formatDate(c.createdAt)} 등록
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              {/* 무한 스크롤 하단 — 자동 로딩 + 수동 더 보기 */}
              {!isLoading && !error && companies.length > 0 && (
                <div className="px-4 py-3 text-center">
                  {isFetchingNextPage ? (
                    <span className="text-xs text-gray-400">더 불러오는 중…</span>
                  ) : hasNextPage ? (
                    <button
                      type="button"
                      onClick={() => fetchNextPage()}
                      className="rounded-md border border-gray-200 px-3 py-1 text-xs text-gray-600 hover:border-primary-500 hover:text-primary-600"
                    >
                      더 보기
                    </button>
                  ) : (
                    <span className="text-xs text-gray-300">마지막 매장입니다</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── 우측: 상세 (7/12) ── */}
        <div className="col-span-12 min-h-0 lg:col-span-7">
          {selectedSeq === null ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
              <div className="text-center">
                <Store className="mx-auto mb-2 text-gray-300" size={40} />
                <div className="text-sm text-gray-500">
                  좌측에서 매장을 선택하면
                  <br />
                  상세 정보가 표시됩니다
                </div>
              </div>
            </div>
          ) : (
            <BusinessDetailPanel
              key={selectedSeq}
              companySeq={selectedSeq}
              onClose={() => setSelectedSeq(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 탭 배지용 건수 조회 — 목록 본문은 버리고 totalElements 만 사용한다.
 */
function useTabCount(search: string | undefined, status: 'Y' | 'N' | 'TEST' | undefined) {
  const { data } = useQuery({
    queryKey: ['companies', 'count', { search, status }],
    queryFn: () => companyApi.getCompanies({ page: 0, size: 1, search, status }),
    select: res => res.totalElements ?? 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  return data;
}
